import { FC, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "@mantine/form";
import styled from "styled-components";

import {
  Button,
  DateInput,
  Grid,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";

import { DateInputFormat, getDateFormat } from "@/utils/date-format";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import UserSelector from "@/components/Forms/components/Selectors/UserSelector";
import validateAsync from "@/components/Forms/utils/zod/validateAsync";
import { isoDateRegex } from "@/components/Forms/utils/zod/regexHelpers";
import {
  EMPTY_VALIDATION_FORM_DATA,
  VALIDATION_FORM_FIELDS,
  ValidationDirtyFields,
  ValidationFormData,
  toNullableString,
} from "./validationForm.utils";
import { ValidationDocumentsField } from "./ValidationDocumentsField";

interface DefaultValidationFormProps {
  DefaultFormData?: Partial<ValidationFormData>;
  // The documents to upload. Owned by the host: mantine's uncontrolled mode
  // never re-renders on input, and the host needs to keep only the files
  // that still have to upload after a partial failure.
  files?: File[];
  onFilesChange?: (files: File[]) => void;
  // After the validation row exists only the documents can still change, so
  // the scalar fields are hidden and the form offers exactly what a retry
  // sends.
  documentsOnly?: boolean;
  // Edit mode (ENG-1000): the row already exists and its reports live in
  // the flyout, so the dropzone is neither rendered nor required.
  showDocuments?: boolean;
  buttonProps?: { cancelText?: string; submitText?: string };
  // Receives the normalized scalar values ("" → null, trimmed) and which
  // fields differ from DefaultFormData (mantine getDirty).
  onSubmit: (
    payload: ValidationFormData,
    dirtyFields: ValidationDirtyFields
  ) => void | Promise<unknown>;
  onCancel: () => void;
  // Lets the host (create modal) lock its own close while a submit is in
  // flight — closing mid-request would otherwise still create the row.
  onSubmittingChange?: (submitting: boolean) => void;
  // Edit-mode hooks (the create flow passes none of these), same contract
  // as DefaultLabAssetForm:
  // Fires whenever the form's dirty state flips, so the host can guard its
  // close with a discard confirmation.
  onDirtyChange?: (dirty: boolean) => void;
  // Disable Save while nothing has changed. PATCH /validations/:uuid rejects
  // an empty body with a 400, so a clean form must never submit.
  disableSubmitWhenClean?: boolean;
  // Keeps the stored Completed By user selectable when the active-only user
  // list excludes them (deactivated, or an observer).
  completedByPinnedOption?: { value: number; label: string };
}

const DOCUMENTS_REQUIRED = "At least one validation document is required";

// Validated against the *normalized* values, so a pasted trailing space
// never fails a rule the payload would have satisfied.
const validationSchema = z
  .object({
    validation_date: z
      .string()
      .regex(
        isoDateRegex,
        `Invalid date format. Please select a date or enter in "YYYY-MM-DD" format.`
      )
      .nullable(),
    next_validation_due: z
      .string()
      .regex(
        isoDateRegex,
        `Invalid date format. Please select a date or enter in "YYYY-MM-DD" format.`
      )
      .nullable(),
    // Matches optionalNullableVersion on the api; without it a long value
    // comes back as a bare "Validation error" toast with no field marked.
    version: z
      .string()
      .max(255, "Version must be 255 characters or fewer")
      .nullable(),
    description: z.string().nullable(),
    // A users.user_id; null is "not recorded".
    completed_by_id: z
      .number({ invalid_type_error: "Invalid user" })
      .int("Invalid user")
      .positive("Invalid user")
      .nullable(),
  })
  // The api takes an asset's due date from its newest validation by
  // validation_date, and an undated row sorts last, so a due date on it
  // would never take effect.
  .superRefine((data, ctx) => {
    if (data.next_validation_due && !data.validation_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["validation_date"],
        message: "Set a Validation Date so the next due date takes effect",
      });
    }
  });

const RetryNotice = styled.div`
  margin-bottom: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const DefaultValidationForm: FC<DefaultValidationFormProps> = ({
  DefaultFormData,
  files = [],
  onFilesChange,
  documentsOnly = false,
  showDocuments = true,
  buttonProps,
  onSubmit,
  onCancel,
  onSubmittingChange,
  onDirtyChange,
  disableSubmitWhenClean = false,
  completedByPinnedOption,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [filesError, setFilesError] = useState<string | undefined>();
  // Ref, not state: two clicks landing before the validation await resolves
  // would both read submitting === false and each POST. The ref flips
  // synchronously so the second click is rejected before any await.
  const submitLockRef = useRef(false);
  // onValuesChange runs from inside mantine's memoized setter chain, so it
  // must not depend on which render's closure fires (see DefaultLabAssetForm).
  const dirtyRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  const updateDirty = (value: boolean) => {
    if (value === dirtyRef.current) return;
    dirtyRef.current = value;
    setDirty(value);
    onDirtyChangeRef.current?.(value);
  };

  const form = useForm<ValidationFormData>({
    mode: "uncontrolled",
    initialValues: { ...EMPTY_VALIDATION_FORM_DATA, ...DefaultFormData },
    // Field-wise compare against the initial values rather than
    // form.isDirty(): uncontrolled mode doesn't re-render on input, and this
    // callback is the only hook for dirty tracking. Values are flat scalars,
    // so strict equality per key is a full compare.
    onValuesChange: (values) => {
      const initial = form.getInitialValues();
      updateDirty(
        VALIDATION_FORM_FIELDS.some((key) => values[key] !== initial[key])
      );
    },
  });

  const updateSubmitting = (value: boolean) => {
    setSubmitting(value);
    onSubmittingChange?.(value);
  };

  const handleFilesChange = (next: File[]) => {
    onFilesChange?.(next);
    if (next.length > 0) setFilesError(undefined);
  };

  const runSubmit = async () => {
    const formData = form.getValues();

    const normalized: ValidationFormData = {
      validation_date: toNullableString(formData.validation_date),
      next_validation_due: toNullableString(formData.next_validation_due),
      version: toNullableString(formData.version),
      description: toNullableString(formData.description),
      completed_by_id: formData.completed_by_id ?? null,
    };

    const validationResult = await validateAsync(validationSchema)(normalized);
    const missingDocuments = showDocuments && files.length === 0;
    if (validationResult?.hasErrors || missingDocuments) {
      if (validationResult?.hasErrors) form.setErrors(validationResult.errors);
      if (missingDocuments) setFilesError(DOCUMENTS_REQUIRED);
      return;
    }

    updateSubmitting(true);
    try {
      await onSubmit?.(normalized, form.getDirty() as ValidationDirtyFields);
    } finally {
      updateSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    try {
      await runSubmit();
    } finally {
      submitLockRef.current = false;
    }
  };

  return (
    <>
      {documentsOnly && (
        <RetryNotice role="status">
          The validation is saved. Upload the remaining documents to finish.
        </RetryNotice>
      )}
      <Grid col={2}>
        {!documentsOnly && (
          <>
            <DateInput
              size="sm"
              variant="outlined"
              label="Validation Date"
              description="Date the validation was performed"
              clearable={true}
              arrow={true}
              enableCalendar={true}
              format={
                getDateFormat({
                  isMoment: true,
                  includeTime: false,
                }) as DateInputFormat
              }
              key={form.key("validation_date")}
              {...form.getInputProps("validation_date")}
            />
            <DateInput
              size="sm"
              variant="outlined"
              label="Next Validation Due"
              description="When the next validation is due. Users who turn on validation reminders in Notification Settings get an email 30, 7, and 1 day before and 1 day after."
              clearable={true}
              arrow={true}
              enableCalendar={true}
              format={
                getDateFormat({
                  isMoment: true,
                  includeTime: false,
                }) as DateInputFormat
              }
              key={form.key("next_validation_due")}
              {...form.getInputProps("next_validation_due")}
            />
            <TextInput
              size="sm"
              variant="outlined"
              label="Version"
              placeholder="Enter version"
              description="Version of the asset or tool that was validated"
              key={form.key("version")}
              {...form.getInputProps("version")}
            />
            {/* Same mantine-uncontrolled wiring as the asset form's Issued
                To: getInputProps supplies defaultValue and the uncontrolled
                key; the onChange override stores the SelectBox's clear
                (undefined) as null so the model stays number | null. */}
            <UserSelector
              label="Completed By"
              description="User who performed this validation."
              query={{ includeInactive: false, includeObservers: false }}
              pinnedOption={completedByPinnedOption}
              key={form.key("completed_by_id")}
              {...form.getInputProps("completed_by_id")}
              onChange={(value) =>
                form.setFieldValue("completed_by_id", value ?? null, {
                  forceUpdate: false,
                })
              }
            />
            <TextAreaInput
              size="sm"
              variant="outlined"
              label="Description"
              placeholder="What was validated and how"
              description="Scope, method, and outcome of the validation."
              colSpan={2}
              minRows={5}
              maxRows={20}
              key={form.key("description")}
              {...form.getInputProps("description")}
            />
          </>
        )}
        {showDocuments && (
          <ValidationDocumentsField
            colSpan={2}
            required
            description="Reports or other evidence of this validation. Uploaded as validation reports on submit."
            files={files}
            onChange={handleFilesChange}
            error={filesError}
            disabled={submitting}
          />
        )}
      </Grid>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          disabled={submitting}
          onClick={onCancel}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          loading={submitting}
          disabled={submitting || (disableSubmitWhenClean && !dirty)}
          onClick={handleSubmit}
        >
          {buttonProps?.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </>
  );
};

export default DefaultValidationForm;
